import $ from '../core/Dom';

/**
 * Deep space starfield.
 *
 * A canvas 2D port of the three.js scene in the design handoff: four depth
 * layers of particles drifting toward the camera, a slowly rotating ring for
 * depth, a parallax camera that eases toward the pointer, and (optionally) a
 * small ship flying a looping path with a fading trail.
 *
 * Points are projected by hand rather than pulling in a 3D library — it's a
 * particle field and a handful of triangles, and it keeps ~600kb of three.js
 * out of the bundle.
 *
 * Props:
 *   ship (bool) — draw the spaceship. Defaults to false.
 */

// Camera and projection
const FOV = 620; // focal length in px
const CAMERA_Z = 620;
const NEAR = 40; // recycle a particle once it gets this close
const FAR = -1700; // and respawn it this far out

// Particle colours, as `r, g, b` so they can be interpolated into rgba()
const WHITE = '255, 255, 255';
const STEEL = '207, 216, 255';
const PERIWINKLE = '159, 180, 255';
const CORAL = '225, 95, 118';
const PEACH = '240, 163, 95';

/* eslint-disable object-curly-newline */
const LAYERS = [
    { count: 900, spread: 2800, size: 1.5, speed: 0.32, color: STEEL, opacity: 0.7 },
    { count: 320, spread: 1900, size: 2.3, speed: 0.85, color: WHITE, opacity: 0.9 },
    { count: 40, spread: 1700, size: 5.5, speed: 1.5, color: CORAL, opacity: 0.85 },
    { count: 28, spread: 1700, size: 5, speed: 1.15, color: PEACH, opacity: 0.8 }
];
/* eslint-enable object-curly-newline */

const RING_COUNT = 320;
const RING_RADIUS = 430;
const TRAIL_LENGTH = 7;

const SPRITE_SIZE = 64;
const GLOW_SPREAD = 1.8; // drawn radius per unit of projected size

export default (el, props = {}) => {
    const $el = $(el);
    const canvas = el;
    const context = canvas.getContext('2d');
    const withShip = !!(props || {}).ship;

    let width = 0;
    let height = 0;
    let originX = 0;
    let originY = 0;

    let layers = [];
    let ring = [];

    // One pre-rendered glow per colour, so the render loop only ever blits.
    // Building a radial gradient per particle per frame is far too expensive.
    const sprites = {};

    // Camera offset, eased toward the pointer
    let pointerX = 0;
    let pointerY = 0;
    let cameraX = 0;
    let cameraY = 0;

    let frame = null;
    let startedAt = 0;

    let resizeObserver = null;
    let visibilityObserver = null;

    const getSprite = color => {
        if (sprites[color]) {
            return sprites[color];
        }

        const sprite = document.createElement('canvas');
        sprite.width = SPRITE_SIZE;
        sprite.height = SPRITE_SIZE;

        const half = SPRITE_SIZE / 2;
        const spriteContext = sprite.getContext('2d');
        const gradient = spriteContext.createRadialGradient(half, half, 0, half, half, half);

        gradient.addColorStop(0, `rgba(${color}, 1)`);
        gradient.addColorStop(0.22, `rgba(${color}, 0.7)`);
        gradient.addColorStop(1, `rgba(${color}, 0)`);

        spriteContext.fillStyle = gradient;
        spriteContext.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);

        sprites[color] = sprite;

        return sprite;
    };

    /**
     * Projects a scene-space point into canvas space. Returns null when the
     * point sits behind the camera.
     */
    const project = (x, y, z) => {
        const depth = CAMERA_Z - z;

        if (depth <= 1) {
            return null;
        }

        const scale = FOV / depth;

        return {
            x: originX + (x - cameraX) * scale,
            y: originY + (y - cameraY) * scale,
            scale
        };
    };

    const drawGlow = (point, size, color, opacity) => {
        const radius = Math.max(size * point.scale * GLOW_SPREAD, 0.6);
        const diameter = radius * 2;

        context.globalAlpha = Math.max(Math.min(opacity, 1), 0);
        context.drawImage(getSprite(color), point.x - radius, point.y - radius, diameter, diameter);
    };

    const createLayers = () => {
        layers = LAYERS.map(config => {
            const particles = [];

            for (let i = 0; i < config.count; i += 1) {
                particles.push({
                    x: (Math.random() - 0.5) * config.spread,
                    y: (Math.random() - 0.5) * config.spread * 0.68,
                    z: FAR * Math.random()
                });
            }

            return { config, particles };
        });
    };

    const createRing = () => {
        ring = [];

        for (let i = 0; i < RING_COUNT; i += 1) {
            const angle = (i / RING_COUNT) * Math.PI * 2;

            ring.push({
                angle,
                radius: RING_RADIUS + Math.random() * 26,
                z: -280 + Math.sin(angle * 3) * 45
            });
        }
    };

    /**
     * Position along the ship's flight path at a given phase — a slow,
     * self-intersecting loop through the scene.
     */
    const shipPathAt = phase => ({
        x: Math.sin(phase) * 700,
        y: Math.sin(phase * 0.73 + 1.1) * 230 + 30,
        z: -330 + Math.cos(phase * 0.91) * 430
    });

    const drawLayers = () => {
        layers.forEach(({ config, particles }) => {
            const sprite = getSprite(config.color);

            particles.forEach(particle => {
                particle.z += config.speed;

                if (particle.z > NEAR) {
                    particle.z = FAR;
                    particle.x = (Math.random() - 0.5) * config.spread;
                    particle.y = (Math.random() - 0.5) * config.spread * 0.68;
                }

                const point = project(particle.x, particle.y, particle.z);

                if (!point) {
                    return;
                }

                const radius = Math.max(config.size * point.scale * GLOW_SPREAD, 0.6);

                context.globalAlpha = config.opacity;
                context.drawImage(sprite, point.x - radius, point.y - radius, radius * 2, radius * 2);
            });
        });
    };

    const drawRing = elapsed => {
        const spin = elapsed * 0.00006;
        const tilt = Math.sin(elapsed * 0.0002) * 0.12;
        const cos = Math.cos(tilt);
        const sin = Math.sin(tilt);

        ring.forEach(particle => {
            const angle = particle.angle + spin;
            const x = Math.cos(angle) * particle.radius;
            const y = Math.sin(angle) * particle.radius * 0.32;

            // Rotate the ring around the x axis
            const point = project(x, (y * cos) - (particle.z * sin), (y * sin) + (particle.z * cos));

            if (point) {
                drawGlow(point, 2.2, PERIWINKLE, 0.4);
            }
        });
    };

    const drawShip = elapsed => {
        const phase = elapsed * 0.00048;
        const position = shipPathAt(phase);
        const ahead = shipPathAt(phase + 0.035);

        // Trail first, so the ship draws over it
        for (let i = 0; i < TRAIL_LENGTH; i += 1) {
            const trail = shipPathAt(phase - (0.028 * (i + 1)));
            const trailPoint = project(trail.x, trail.y, trail.z);

            if (trailPoint) {
                drawGlow(trailPoint, 5 - (i * 0.45), CORAL, 0.34 * (1 - (i / TRAIL_LENGTH)));
            }
        }

        const point = project(position.x, position.y, position.z);
        const aheadPoint = project(ahead.x, ahead.y, ahead.z);

        if (!point || !aheadPoint) {
            return;
        }

        // Engine glow, pulsing slightly
        drawGlow(point, 8, PEACH, 0.85 + (Math.sin(elapsed * 0.02) * 0.1));

        // Face the direction of travel
        const heading = Math.atan2(aheadPoint.y - point.y, aheadPoint.x - point.x);
        const size = Math.max(13 * point.scale, 3);

        context.globalAlpha = 1;
        context.save();
        context.translate(point.x, point.y);
        context.rotate(heading);

        // Wings
        context.fillStyle = '#8b90a4';
        context.beginPath();
        context.moveTo(-size * 0.2, 0);
        context.lineTo(-size * 0.9, -size * 1.1);
        context.lineTo(-size * 0.55, 0);
        context.lineTo(-size * 0.9, size * 1.1);
        context.closePath();
        context.fill();

        // Hull
        context.fillStyle = '#e9ecf5';
        context.beginPath();
        context.moveTo(size * 1.5, 0);
        context.lineTo(-size * 0.7, -size * 0.42);
        context.lineTo(-size * 0.7, size * 0.42);
        context.closePath();
        context.fill();

        // Canopy
        context.fillStyle = '#0b0d15';
        context.beginPath();
        context.ellipse(size * 0.3, 0, size * 0.36, size * 0.2, 0, 0, Math.PI * 2);
        context.fill();

        context.restore();
    };

    const render = now => {
        const elapsed = now - startedAt;

        // Ease the camera toward the pointer
        cameraX += ((pointerX * 90) - cameraX) * 0.03;
        cameraY += ((-pointerY * 55) - cameraY) * 0.03;

        context.clearRect(0, 0, width, height);

        // Particles are additive, like the reference's AdditiveBlending
        context.globalCompositeOperation = 'lighter';

        drawLayers();
        drawRing(elapsed);

        if (withShip) {
            drawShip(elapsed);
        }

        context.globalAlpha = 1;
        context.globalCompositeOperation = 'source-over';

        frame = window.requestAnimationFrame(render);
    };

    const start = () => {
        if (frame) {
            return;
        }

        if (!startedAt) {
            startedAt = window.performance.now();
        }

        frame = window.requestAnimationFrame(render);
    };

    const stop = () => {
        if (frame) {
            window.cancelAnimationFrame(frame);
            frame = null;
        }
    };

    const resize = () => {
        width = $el.width();
        height = $el.height();

        if (!width || !height) {
            return;
        }

        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);

        originX = width / 2;
        originY = height / 2;

        context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onPointerMove = e => {
        // The canvas sits behind the hero content, so pointer position has to
        // come off the window rather than from events on the canvas itself
        const rect = canvas.getBoundingClientRect();

        pointerX = ((e.clientX - rect.left) / rect.width) - 0.5;
        pointerY = ((e.clientY - rect.top) / rect.height) - 0.5;
    };

    const onVisibilityChange = entries => {
        if (entries.some(entry => entry.isIntersecting)) {
            start();
        } else {
            stop();
        }
    };

    const init = () => {
        // None of this means anything without motion, so opt out entirely
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        resize();

        if (!width || !height) {
            return;
        }

        createLayers();
        createRing();

        window.addEventListener('pointermove', onPointerMove, { passive: true });

        if (window.ResizeObserver) {
            resizeObserver = new window.ResizeObserver(resize);
            resizeObserver.observe(el);
        } else {
            window.addEventListener('resize', resize);
        }

        // Don't burn frames animating a hero that's scrolled out of view
        if (window.IntersectionObserver) {
            visibilityObserver = new window.IntersectionObserver(onVisibilityChange);
            visibilityObserver.observe(el);
        }

        start();
    };

    const destroy = () => {
        stop();

        window.removeEventListener('pointermove', onPointerMove);

        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        } else {
            window.removeEventListener('resize', resize);
        }

        if (visibilityObserver) {
            visibilityObserver.disconnect();
            visibilityObserver = null;
        }

        layers = [];
        ring = [];
    };

    return {
        init,
        destroy
    };
};
